import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardCards from '../components/dashboard/DashboardCards';
import MonthlyEvolutionChart from '../components/dashboard/MonthlyEvolutionChart';
import RevenueExpenseChart from '../components/dashboard/RevenueExpenseChart';
import ProductDistributionChart from '../components/dashboard/ProductDistributionChart';
import ExpensesByCategoryChart from '../components/dashboard/ExpensesByCategoryChart';
import TopProductsRanking from '../components/dashboard/TopProductsRanking';
import { useAppContext } from '../context/AppContext';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../context/AuthContext';
import UpgradeModal from '../components/common/UpgradeModal';
import OnboardingTour from '../components/common/OnboardingTour';

export default function DashboardPage() {
    const { products, sales, expenses } = useAppContext();
    const { hasFeature } = usePermissions();
    const { userProfile } = useAuth();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);

    const canViewCharts = hasFeature('dashboardCharts');
    const currentPlan = userProfile?.plan || 'basico';

    // Verificar se é o primeiro acesso do usuário
    useEffect(() => {
        const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');

        // Mostrar onboarding apenas se:
        // 1. Nunca viu o onboarding antes
        // 2. Não tem produtos cadastrados (novo usuário)
        if (!hasSeenOnboarding && products.length === 0 && sales.length === 0) {
            // Delay de 1s para o usuário se acostumar com a tela
            const timer = setTimeout(() => {
                setShowOnboarding(true);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [products.length, sales.length]);

    const handleOnboardingComplete = () => {
        setShowOnboarding(false);
        localStorage.setItem('hasSeenOnboarding', 'true');
    };

    return (
        <>
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0">Dashboard - Visão Geral do Negócio</h1>
                    <span className="text-muted small">
                        <i className="bi bi-box me-1"></i>{products.length} produtos · <i className="bi bi-cart ms-2 me-1"></i>{sales.length} vendas · <i className="bi bi-cash-coin ms-2 me-1"></i>{expenses.length} despesas
                    </span>
                </div>

                {/* Cards de métricas principais - sempre visíveis */}
                <DashboardCards />

                {/* Gráficos - visíveis apenas para planos pagos */}
                {canViewCharts ? (
                    <>
                        <div className="row">
                            <div className="col-lg-12">
                                <MonthlyEvolutionChart />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-8">
                                <RevenueExpenseChart />
                            </div>
                            <div className="col-lg-4">
                                <TopProductsRanking />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-6">
                                <ProductDistributionChart />
                            </div>
                            <div className="col-lg-6">
                                <ExpensesByCategoryChart />
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="card shadow-sm border-warning">
                        <div className="card-body text-center py-5">
                            <div className="mb-4" style={{ fontSize: '3rem' }}>
                                <i className="bi bi-bar-chart-fill text-warning"></i>
                            </div>
                            <h4 className="mb-3">Gráficos e Análises Avançadas</h4>
                            <p className="text-muted mb-4">
                                Visualize a evolução do seu negócio com gráficos detalhados.
                                Disponível nos planos <strong>Profissional</strong> e <strong>Premium</strong>.
                            </p>

                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title"><i className="bi bi-graph-up me-2"></i>Evolução Mensal</h6>
                                            <p className="card-text small text-muted mb-0">
                                                Acompanhe receitas, despesas e lucro mês a mês
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title"><i className="bi bi-trophy me-2"></i>Ranking de Produtos</h6>
                                            <p className="card-text small text-muted mb-0">
                                                Veja quais produtos vendem mais
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title"><i className="bi bi-box me-2"></i>Distribuição de Produtos</h6>
                                            <p className="card-text small text-muted mb-0">
                                                Análise por tipo de produto
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title"><i className="bi bi-cash-coin me-2"></i>Despesas por Categoria</h6>
                                            <p className="card-text small text-muted mb-0">
                                                Identifique onde seu dinheiro está indo
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex gap-3 justify-content-center">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={() => setShowUpgradeModal(true)}
                                >
                                    🚀 Desbloquear Gráficos
                                </button>
                                <Link to="/planos" className="btn btn-outline-primary btn-lg">
                                    Ver Todos os Planos
                                </Link>
                            </div>

                            <p className="text-muted small mt-4 mb-0">
                                Você está no <strong>Plano Básico (Gratuito)</strong> • Faça upgrade para R$ 29,90/mês
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <UpgradeModal
                show={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                feature="dashboardCharts"
            />

            <OnboardingTour
                isOpen={showOnboarding}
                onComplete={handleOnboardingComplete}
            />
        </>
    );
}
